/**
 * @class AesEncriptar
 * @brief Implementa las funciones de encriptacion y desencriptacion utilizando AES-GCM.
 */
class AesEncriptar {

    /** @brief Longitud en bits del tag de autenticación GCM. */
    TAG_LENGTH = 128;
    /** @brief Longitud en bytes del vector de inicialización (IV) para GCM. */
    IV_LENGTH = 12;

    /**
     * @brief Encripta un objeto o cadena de texto utilizando AES-GCM.
     * @param jsonObject Objeto o candena que se desea encriptar. 
     * @param keyB64 Llave de encriptacion en base64.
     * @returns {Promise<Object>} Objeto con los datos encriptados, incluyendo textoCifrado, iv y hmac.
     */
    async encriptar(jsonObject, keyB64) {

        if (!keyB64 || keyB64.length < 16) {
            console.warn("Abortando encriptación: No hay una llave válida disponible.");
            return null;
        }

        const data = typeof jsonObject === 'object' ? JSON.stringify(jsonObject) : jsonObject;
        const keyData = Uint8Array.from(atob(keyB64), c => c.charCodeAt(0));
        const encoder = new TextEncoder();
        const dataUint8 = encoder.encode(data);

        // Importar la llave
        const cryptoKey = await window.crypto.subtle.importKey(
            "raw", keyData, { name: "AES-GCM" }, false, ["encrypt"]
        );

        // Generar IV de 12 bytes
        const iv = window.crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

        // Encriptar
        const encrypted = await window.crypto.subtle.encrypt(
            { name: "AES-GCM", iv: iv, tagLength: this.TAG_LENGTH },
            cryptoKey,
            dataUint8
        );

        return {
            textoCifrado: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
            iv: btoa(String.fromCharCode(...iv)),
            hmac: "GCM_AUTHENTICATED" // Mantener compatibilidad con tu modelo
        };
    }
    /**
     * @brief Desencripta un objeto encriptado utilizando AES-GCM.
     *
     * @param hmacRequestObject Objeto con los datos encriptados (textoCifrado e iv).
     * @param keyB64 Llave de desencriptación en base64.
     *
     * @returns {Promise<Object>} Objeto desencriptado con los datos originales.
     * @throws {Error} Lanza un error si la desencriptación falla debido a integridad o llave incorrecta.
     */
    async desencriptar(hmacRequestObject, keyB64) {
        const { textoCifrado, iv } = hmacRequestObject;
        const keyData = Uint8Array.from(atob(keyB64), c => c.charCodeAt(0));
        const ivData = Uint8Array.from(atob(iv), c => c.charCodeAt(0));
        const encryptedData = Uint8Array.from(atob(textoCifrado), c => c.charCodeAt(0));

        const cryptoKey = await window.crypto.subtle.importKey(
            "raw", keyData, { name: "AES-GCM" }, false, ["decrypt"]
        );

        try {
            const decrypted = await window.crypto.subtle.decrypt(
                { name: "AES-GCM", iv: ivData, tagLength: this.TAG_LENGTH },
                cryptoKey,
                encryptedData
            );

            const decoder = new TextDecoder();
            const decodedString = decoder.decode(decrypted);
            return JSON.parse(decodedString);
        } catch (e) {
            console.error("Fallo de integridad o llave incorrecta", e);
            throw new Error("No se pudo desencriptar");
        }
    }
}

export default new AesEncriptar();